import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface questionMap {
  question_id: string;
  index: number;
  answered: boolean;
  submitted_option: string;
  visited: boolean;
  dirty?: boolean;
  question_type?: string;
}

export interface userDetails {
  name: string;
  email: string;
}

export interface assessmentState {
  current_question_number: number;
  total_question: number;
  total_time: number;
  assessment_name: string;
  company_name: string;
  question_answered: number;
  time_remaining: number;
  questions_map: questionMap[] | [];
  is_adaptive: boolean;
  photo_upload_pause: boolean;
  objective_question?: number;
  text_question?: number;
  video_question?: number;
  coding_question?: number;
  camera_permission?: boolean;
  camera_required: boolean;
  is_bot: boolean;
  skills: string[];
}

const initialState: assessmentState = {
  current_question_number: 1,
  total_question: 0,
  total_time: 0,
  assessment_name: '',
  company_name: '',
  question_answered: 0,
  time_remaining: Infinity,
  questions_map: [],
  is_adaptive: false,
  photo_upload_pause: false,
  objective_question: 0,
  coding_question: 0,
  camera_permission: true,
  camera_required: true,
  video_question: 0,
  is_bot: false,
  skills: [],
};

export const assessmentSlice = createSlice({
  name: 'assessment',
  initialState,
  reducers: {
    setAssessmentInfo: (state, action: PayloadAction<assessmentState>) => {
      const {
        total_question,
        total_time,
        assessment_name,
        company_name,
        is_adaptive,
        skills = [],
        camera_required = true,
      } = action.payload;
      state.total_question = total_question;
      state.total_time = total_time;
      state.assessment_name = assessment_name;
      state.company_name = company_name;
      state.is_adaptive = is_adaptive;
      state.skills = skills;
      state.camera_required = camera_required;
    },
    setCurrentQuestionNumber: (state, action: PayloadAction<number>) => {
      state.current_question_number = action.payload;
    },
    setQuestionsAnswered: (state, action: PayloadAction<number>) => {
      state.question_answered = action.payload;
    },
    setAssessmentTimeRemaining: (state, action: PayloadAction<number>) => {
      state.time_remaining = action.payload;
    },
    setQuestionsMap: (state, action: PayloadAction<questionMap[]>) => {
      state.questions_map = action.payload;
    },
    setQuestionDirty: (state, action: PayloadAction<questionMap[]>) => {
      state.questions_map = action.payload;
    },
    setQuestionNotDirty: (state, action: PayloadAction<questionMap[]>) => {
      state.questions_map = action.payload;
    },
    setAdaptive: (state, action: PayloadAction<boolean>) => {
      state.is_adaptive = action.payload;
    },
    setPhotoUploadPause: (state, action: PayloadAction<boolean>) => {
      state.photo_upload_pause = action.payload;
    },
    setCodingQuestion: (state, action: PayloadAction<number>) => {
      state.coding_question = action.payload;
    },
    setTextualQuestion: (state, action: PayloadAction<number>) => {
      state.text_question = action.payload;
    },
    setIsBot: (state, action: PayloadAction<boolean>) => {
      state.is_bot = action.payload;
    },
    setObjectiveQuestion: (state, action: PayloadAction<number>) => {
      state.objective_question = action.payload;
    },

    disableCameraPermission: (state) => {
      state.camera_permission = false;
    },
    setVideoQuestionCount: (state, action: PayloadAction<number>) => {
      state.video_question = action.payload;
    },
  },
});

export const {
  setAssessmentInfo,
  setQuestionsAnswered,
  setCurrentQuestionNumber,
  setAssessmentTimeRemaining,
  setQuestionsMap,
  setAdaptive,
  setPhotoUploadPause,
  setCodingQuestion,
  setTextualQuestion,
  setObjectiveQuestion,
  disableCameraPermission,
  setVideoQuestionCount,
  setIsBot,
} = assessmentSlice.actions;

export default assessmentSlice.reducer;
